export interface ReportTypeItem {
  /** Stable key for app logic (should not be localized). */
  type: ReportTypeKey;
  /** Report category for landing-page grouping. */
  categoryId: ReportCategoryId;
  /** i18n key for UI label. */
  labelKey: string;
  /** i18n key for UI description. */
  descriptionKey: string;
  /** Backend type string used in API queries (must not depend on localized label). */
  apiType: string;
  /** URL-friendly slug (kebab-case) for routing. */
  slug: string;
}

export const REPORT_CATEGORY_IDS = [
  'capital-events',
  'closing',
  'distributions',
  'reporting',
  'reminders',
  'governance',
] as const;

export type ReportCategoryId = (typeof REPORT_CATEGORY_IDS)[number];

type ReportTypeCatalogEntry = {
  type: string;
  categoryId: ReportCategoryId;
  labelKey: string;
  descriptionKey: string;
  apiType: string;
  slug: string;
};

export const REPORT_TYPE_CATALOG = [
  {
    type: 'CAPITAL_CALL',
    categoryId: 'capital-events',
    labelKey: 'translations.newReports.reportTypes.capitalCall.label',
    apiType: 'Capital Call',
    slug: 'capital-call',
    descriptionKey:
      'translations.newReports.reportTypes.capitalCall.description',
  },
  {
    type: 'EQUALISATION_CAPITAL_CALL',
    categoryId: 'capital-events',
    labelKey: 'translations.newReports.reportTypes.equalisationCapitalCall.label',
    apiType: 'Equalisation Capital Call',
    slug: 'equalisation-capital-call',
    descriptionKey:
      'translations.newReports.reportTypes.equalisationCapitalCall.description',
  },
  {
    type: 'CLOSING_CONFIRMATION',
    categoryId: 'closing',
    labelKey: 'translations.newReports.reportTypes.closingConfirmation.label',
    apiType: 'Closing Confirmation',
    slug: 'closing-confirmation',
    descriptionKey:
      'translations.newReports.reportTypes.closingConfirmation.description',
  },
  {
    type: 'CLOSING_NOTICE',
    categoryId: 'closing',
    labelKey: 'translations.newReports.reportTypes.closingNotice.label',
    apiType: 'Closing Notice',
    slug: 'closing-notice',
    descriptionKey:
      'translations.newReports.reportTypes.closingNotice.description',
  },
  {
    type: 'DISTRIBUTION_NOTICE',
    categoryId: 'distributions',
    labelKey: 'translations.newReports.reportTypes.distributionNotice.label',
    apiType: 'Distribution Notice',
    slug: 'distribution-notice',
    descriptionKey:
      'translations.newReports.reportTypes.distributionNotice.description',
  },
  {
    type: 'EQUALISATION_DISTRIBUTION',
    categoryId: 'distributions',
    labelKey: 'translations.newReports.reportTypes.equalisationDistribution.label',
    apiType: 'Equalisation Distribution',
    slug: 'equalisation-distribution',
    descriptionKey:
      'translations.newReports.reportTypes.equalisationDistribution.description',
  },
  {
    type: 'TAX_REPORTS',
    categoryId: 'reporting',
    labelKey: 'translations.newReports.reportTypes.taxReports.label',
    apiType: 'Tax Reports',
    slug: 'tax-reports',
    descriptionKey:
      'translations.newReports.reportTypes.taxReports.description',
  },
  {
    type: 'CONFIRMATION_STATEMENT',
    categoryId: 'reporting',
    labelKey: 'translations.newReports.reportTypes.confirmationStatement.label',
    apiType: 'Confirmation Statement',
    slug: 'confirmation-statement',
    descriptionKey:
      'translations.newReports.reportTypes.confirmationStatement.description',
  },
  {
    type: 'QUARTERLY_REPORT',
    categoryId: 'reporting',
    labelKey: 'translations.newReports.reportTypes.quarterlyReport.label',
    apiType: 'Quarterly Report',
    slug: 'quarterly-report',
    descriptionKey:
      'translations.newReports.reportTypes.quarterlyReport.description',
  },
  {
    type: 'PAYMENT_REMINDER',
    categoryId: 'reminders',
    labelKey: 'translations.newReports.reportTypes.paymentReminder.label',
    apiType: 'Payment Reminder',
    slug: 'payment-reminder',
    descriptionKey:
      'translations.newReports.reportTypes.paymentReminder.description',
  },
  {
    type: 'OVERDUE_REMINDER',
    categoryId: 'reminders',
    labelKey: 'translations.newReports.reportTypes.overdueReminder.label',
    apiType: 'Overdue Reminder',
    slug: 'overdue-reminder',
    descriptionKey:
      'translations.newReports.reportTypes.overdueReminder.description',
  },
  {
    type: 'POA_REMINDER',
    categoryId: 'reminders',
    labelKey: 'translations.newReports.reportTypes.poaReminder.label',
    apiType: 'PoA Reminder',
    slug: 'poa-reminder',
    descriptionKey: 'translations.newReports.reportTypes.poaReminder.description',
  },
  {
    type: 'RESOLUTION_REMINDER',
    categoryId: 'reminders',
    labelKey: 'translations.newReports.reportTypes.resolutionReminder.label',
    apiType: 'Resolution Reminder',
    slug: 'resolution-reminder',
    descriptionKey:
      'translations.newReports.reportTypes.resolutionReminder.description',
  },
  {
    type: 'SHAREHOLDER_RESOLUTION',
    categoryId: 'governance',
    labelKey: 'translations.newReports.reportTypes.shareholderResolution.label',
    apiType: 'Shareholder Resolution',
    slug: 'shareholder-resolution',
    descriptionKey:
      'translations.newReports.reportTypes.shareholderResolution.description',
  },
  {
    type: 'GENERAL_NOTICE',
    categoryId: 'governance',
    labelKey: 'translations.newReports.reportTypes.generalNotice.label',
    apiType: 'General Notice',
    slug: 'general-notice',
    descriptionKey: 'translations.newReports.reportTypes.generalNotice.description',
  },
  {
    type: 'SEMI_PROFESSIONAL_CONFIRMATION',
    categoryId: 'governance',
    labelKey:
      'translations.newReports.reportTypes.semiProfessionalConfirmation.label',
    apiType: 'Semi-professional Confirmation',
    slug: 'semi-professional-confirmation',
    descriptionKey:
      'translations.newReports.reportTypes.semiProfessionalConfirmation.description',
  },
] as const satisfies ReadonlyArray<ReportTypeCatalogEntry>;

export type ReportTypeKey = (typeof REPORT_TYPE_CATALOG)[number]['type'];

export const REPORT_TYPE_BY_KEY: Record<
  ReportTypeKey,
  (typeof REPORT_TYPE_CATALOG)[number]
> = REPORT_TYPE_CATALOG.reduce(
  (acc, item) => {
    acc[item.type] = item;
    return acc;
  },
  {} as Record<ReportTypeKey, (typeof REPORT_TYPE_CATALOG)[number]>
);

export const REPORT_TYPE_BY_SLUG: Record<
  string,
  (typeof REPORT_TYPE_CATALOG)[number]
> = REPORT_TYPE_CATALOG.reduce(
  (acc, item) => {
    acc[item.slug] = item;
    return acc;
  },
  {} as Record<string, (typeof REPORT_TYPE_CATALOG)[number]>
);
